import React, { useState } from 'react'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { GripVertical, Edit, ArrowUp, ArrowDown, Info } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from 'react-query'
import { dataInputsService } from '../services/dataInputs'
import { smartCacheInvalidation } from '../utils/cacheInvalidation'
import { toast } from 'react-hot-toast'



const DraggableAttributesList = ({
  attributes,
  dataInputId,
  hasEditPermission = false,
  onReorderComplete
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const queryClient = useQueryClient()

  // Reorder mutation
  const reorderMutation = useMutation(
    (reorderedAttributes) => dataInputsService.reorderAttributes(dataInputId, reorderedAttributes),
    {
      onSuccess: () => {
        toast.success('Attributes reordered successfully!')
        // Smart cache invalidation for real-time updates
        smartCacheInvalidation(queryClient, 'attribute', 'update', dataInputId)
        if (onReorderComplete) {
          onReorderComplete()
        }
      },
      onError: (error) => {
        console.error('Error reordering attributes:', error)
        toast.error('Failed to reorder attributes')
      }
    }
  )

  const handleDragStart = () => {
    setIsDragging(true)
  }

  const handleDragEnd = (result) => {
    setIsDragging(false)

    // If dropped outside the list or no movement
    if (!result.destination || result.destination.index === result.source.index) {
      return
    }

    // Reorder the attributes array
    const reorderedAttributes = Array.from(attributes)
    const [removed] = reorderedAttributes.splice(result.source.index, 1)
    reorderedAttributes.splice(result.destination.index, 0, removed)

    // Update display_order for all attributes
    const attributesWithNewOrder = reorderedAttributes.map((attr, index) => ({
      ...attr,
      display_order: index
    }))

    // Call the API to update the order
    reorderMutation.mutate(attributesWithNewOrder)
  }

  const getDataTypeIcon = (dataType) => {
    switch (dataType.toLowerCase()) {
      case 'text':
      case 'email':
      case 'url':
        return '📝'
      case 'number':
      case 'decimal':
        return '🔢'
      case 'date':
      case 'datetime':
        return '📅'
      case 'boolean':
        return '☑️'
      case 'phone number':
        return '📞'
      default:
        return '📄'
    }
  }

  const getDataTypeColor = (dataType) => {
    switch (dataType.toLowerCase()) {
      case 'text':
      case 'email':
      case 'url':
        return 'bg-blue-100 text-blue-800'
      case 'number':
      case 'decimal':
        return 'bg-green-100 text-green-800'
      case 'date':
      case 'datetime':
        return 'bg-purple-100 text-purple-800'
      case 'boolean':
        return 'bg-orange-100 text-orange-800'
      case 'phone number':
        return 'bg-teal-100 text-teal-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (!attributes || attributes.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <div className="text-4xl mb-2">📋</div>
        <p>No attributes found</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Column Order Warning */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-900">Column Order Information</h4>
            <p className="text-sm text-blue-800 mt-1">
              The order of attributes here determines the column order in the Records tab.
              Drag and drop to reorder attributes, which will automatically update the column layout.
            </p>
          </div>
        </div>
      </div>

      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <Droppable droppableId="attributes-list">
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className={`space-y-2 ${
              snapshot.isDraggingOver ? 'bg-blue-50 rounded-lg p-2' : ''
            }`}
          >
            {attributes.map((attribute, index) => (
              <Draggable
                key={attribute.id}
                draggableId={attribute.id.toString()}
                index={index}
                isDragDisabled={!hasEditPermission || reorderMutation.isLoading}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    className={`
                      bg-white border border-gray-200 rounded-lg p-4 transition-all duration-200
                      ${snapshot.isDragging ? 'shadow-lg rotate-2 bg-blue-50' : 'hover:shadow-md'}
                      ${isDragging && !snapshot.isDragging ? 'opacity-50' : ''}
                      ${!hasEditPermission ? 'cursor-default' : 'cursor-move'}
                    `}
                  >
                    <div className="flex items-center space-x-3">
                      {/* Drag Handle */}
                      {hasEditPermission && (
                        <div
                          {...provided.dragHandleProps}
                          className="flex-shrink-0 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                        >
                          <GripVertical className="h-5 w-5" />
                        </div>
                      )}

                      {/* Attribute Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          {/* Data Type Icon */}
                          <span className="text-lg" title={attribute.data_type}>
                            {getDataTypeIcon(attribute.data_type)}
                          </span>

                          {/* Name and Label */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <h4 className="text-sm font-medium text-gray-900 truncate">
                                {attribute.label || attribute.name}
                              </h4>
                              {attribute.label && (
                                <span className="text-xs text-gray-500 font-mono">
                                  ({attribute.name})
                                </span>
                              )}
                            </div>
                            {attribute.description && (
                              <p className="text-xs text-gray-600 mt-1 truncate">
                                {attribute.description}
                              </p>
                            )}
                          </div>

                          {/* Data Type Badge */}
                          <span className={`
                            inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                            ${getDataTypeColor(attribute.data_type)}
                          `}>
                            {attribute.data_type}
                          </span>

                          {/* Status and Flags */}
                          <div className="flex items-center space-x-1">
                            {attribute.is_required && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                Required
                              </span>
                            )}
                            {attribute.is_unique && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                Unique
                              </span>
                            )}
                            {!attribute.is_active && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                Inactive
                              </span>
                            )}
                          </div>

                          {/* Actions */}
                          {hasEditPermission && (
                            <div className="flex items-center space-x-1">
                              <Link
                                to={`/data-inputs/${dataInputId}/attributes/${attribute.id}/edit`}
                                className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                                title="Edit Attribute"
                              >
                                <Edit className="h-4 w-4" />
                              </Link>
                            </div>
                          )}
                        </div>

                        {/* Value Options Preview */}
                        {attribute.allowed_values && attribute.allowed_values.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-500">Options:</span>
                              <div className="flex flex-wrap gap-1">
                                {attribute.allowed_values.slice(0, 3).map((value, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
                                  >
                                    {value}
                                  </span>
                                ))}
                                {attribute.allowed_values.length > 3 && (
                                  <span className="text-xs text-gray-500">
                                    +{attribute.allowed_values.length - 3} more
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
    </div>
  )
}

export default DraggableAttributesList
